import React, { useLayoutEffect, useRef, useState } from 'react';
import './UspStrip.css';

interface UspStripProps {
  items: string[];
  className?: string;
}

const UspStrip: React.FC<UspStripProps> = ({ items, className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cycleRef = useRef<HTMLDivElement>(null);
  const [copies, setCopies] = useState(1);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const cycle = cycleRef.current;
    if (!container || !cycle) return;

    const measure = () => {
      const width = cycle.getBoundingClientRect().width;
      if (width > 0) setCopies(Math.max(1, Math.ceil(container.clientWidth / width)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    observer.observe(cycle);
    return () => observer.disconnect();
  }, [items]);

  if (!items || items.length === 0) return null;

  const renderCycle = (key: string, hidden: boolean, first = false) => (
    <div
      key={key}
      ref={first ? cycleRef : undefined}
      className={`usp-strip-cycle flex items-center shrink-0${hidden ? ' usp-strip-cycle-duplicate' : ''}`}
      aria-hidden={hidden || undefined}
    >
      {items.map((item, idx) => (
        <React.Fragment key={idx}>
          <span className="px-6 sm:px-10">{item}</span>
          <span className="text-white/60 select-none usp-strip-separator" aria-hidden="true">|</span>
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div ref={containerRef} className={`w-full bg-[#78BE20] text-white py-3.5 overflow-hidden usp-strip-container ${className}`}>
      <div
        className="usp-strip-track flex items-center font-poppins font-bold text-xs sm:text-[13px] tracking-[0.14em] uppercase whitespace-nowrap"
        style={{ animationDuration: `${25 * copies}s` }}
      >
        {/* Both equal-width groups must cover the viewport for a seamless loop. */}
        <div className="usp-strip-content flex items-center shrink-0">
          {Array.from({ length: copies }, (_, i) => renderCycle(`orig-${i}`, i > 0, i === 0))}
        </div>
        <div className="usp-strip-duplicate flex items-center shrink-0" aria-hidden="true">
          {Array.from({ length: copies }, (_, i) => renderCycle(`dup-${i}`, true))}
        </div>
      </div>
    </div>
  );
};

export default UspStrip;
